import { useMemo } from 'react';
import { Download, Loader2, X } from 'lucide-react';

interface DocumentPreviewModalProps {
  isOpen: boolean;
  title: string;
  loading?: boolean;
  error?: string | null;
  blobUrl?: string | null;
  mimeType?: string | null;
  onClose: () => void;
  onDownload?: () => void;
}

export function DocumentPreviewModal({
  isOpen,
  title,
  loading = false,
  error = null,
  blobUrl = null,
  mimeType = null,
  onClose,
  onDownload,
}: DocumentPreviewModalProps) {
  const isImage = useMemo(() => !!mimeType && mimeType.startsWith('image/'), [mimeType]);
  const canEmbed = useMemo(() => !!blobUrl && !isImage, [blobUrl, isImage]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-5xl h-[85vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 truncate pr-4">{title}</h3>
          <div className="flex items-center gap-2 shrink-0">
            {onDownload && blobUrl && (
              <button
                type="button"
                onClick={onDownload}
                className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Télécharger
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              title="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 p-4 bg-gray-50">
          {loading && (
            <div className="h-full flex items-center justify-center text-gray-600 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Chargement du document...
            </div>
          )}

          {!loading && error && (
            <div className="h-full flex items-center justify-center text-red-600 text-sm px-4 text-center">
              {error}
            </div>
          )}

          {!loading && !error && blobUrl && canEmbed && (
            <iframe
              src={blobUrl}
              title={title}
              className="w-full h-full rounded-lg border border-gray-300 bg-white"
            />
          )}

          {!loading && !error && blobUrl && isImage && (
            <div className="h-full flex items-center justify-center overflow-auto">
              <img src={blobUrl} alt={title} className="max-w-full max-h-full object-contain rounded-lg" />
            </div>
          )}

          {!loading && !error && blobUrl && !canEmbed && !isImage && (
            <div className="h-full flex items-center justify-center">
              <div className="bg-white border border-gray-200 rounded-lg p-6 text-center max-w-md">
                <p className="text-sm text-gray-700 mb-4">
                  L&apos;aperçu direct n&apos;est pas disponible pour ce format. Téléchargez le fichier pour le consulter.
                </p>
                {onDownload && (
                  <button
                    type="button"
                    onClick={onDownload}
                    className="px-3 py-2 text-sm rounded-md border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Télécharger
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
